import { Component, inject, OnInit, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { Store } from '@ngrx/store';
import { filter, switchMap, take, tap } from 'rxjs/operators';

import { Observable } from 'rxjs';

import {
  AdblockDetectorService,
  ConversationSenderTypes,
  EnvironmentConfig,
  GetSystemDowntime,
  getTranslationLoaded,
  Go,
  I18nService,
  InitCookiesPreference,
  LoadingSpinnerComponent,
  ModalService,
  NewRelicService,
  NewUpdateBannerComponent,
  PageTitleService,
  RoutingDetectorService,
  ScrollerDirective,
  SetSender,
  SetViewportSize,
  ThemeDirective,
  ThemeService,
  ToastService,
  UpdateApplication,
  ViewportSizeDetectorService
} from '@ui/legacy-lib';

import { environment } from 'landlord-env';
import { AsyncPipe } from '@angular/common';
import { NAVIGATION_LINK } from '@ui/landlord/shared/config';
import { SharedUserStore } from '@ui/landlord/shared/state/user';
import { toObservable } from '@angular/core/rxjs-interop';
import * as fromAppState from './+state';
import * as fromAccountState from './screens/account/+state';

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    LoadingSpinnerComponent,
    AsyncPipe,
    RouterOutlet,
    ThemeDirective,
    ScrollerDirective
  ]
})
export class AppComponent implements OnInit {
  toastService = inject(ToastService);
  viewContainerRef = inject(ViewContainerRef);
  private store = inject<Store<fromAppState.AppState>>(Store);
  private viewportSizeDetector = inject(ViewportSizeDetectorService);
  private activatedRoute = inject(ActivatedRoute);
  private pageTitleService = inject(PageTitleService);
  private i18nService = inject(I18nService);
  private routingDetectorService = inject(RoutingDetectorService);
  private adBlockDetectorService = inject(AdblockDetectorService);
  private themeService = inject(ThemeService);
  private newRelicService = inject(NewRelicService);
  private swUpdate = inject(SwUpdate);
  private modalService = inject(ModalService);
  private sharedUserStore = inject(SharedUserStore);

  public finishedLoaded$: Observable<boolean>;

  constructor() {
    const viewContainerRef = this.viewContainerRef;

    this.pageTitleService.run(this.activatedRoute);
    this.i18nService.loadDefaultTranslations();
    this.toastService.setRootViewContainerRef(viewContainerRef);
    this.routingDetectorService.start();
    // this.newRelicService.init();
    this.store.dispatch(new GetSystemDowntime());
    this.store.dispatch(
      new SetViewportSize(this.viewportSizeDetector.detectSize())
    );
    this.store.dispatch(new SetSender(ConversationSenderTypes.LANDLORD));

    this.store
      .select(fromAccountState.getUserDataLoaded)
      .pipe(
        filter(loaded => loaded),
        tap(() => {
          this.store.dispatch(new fromAccountState.LoadCustomerProduct());
          this.store.dispatch(new fromAccountState.GetEmailTemplateTypes());
        }),
        switchMap(() =>
          this.store.select(fromAccountState.getHasActiveProduct)
        ),
        filter(hasActiveProduct => !hasActiveProduct),
        take(1)
      )
      .subscribe(() =>
        this.store.dispatch(
          new Go({
            path: [NAVIGATION_LINK.MANAGE_BOOKING]
          })
        )
      );

    this.finishedLoaded$ = this.store.select(getTranslationLoaded);
    this.themeService.init(environment);
    toObservable(this.sharedUserStore.customerSettings)
      .pipe(
        filter(settings => !!settings),
        untilDestroyed(this)
      )
      .subscribe(settings => {
        const theme = settings.brandingThemes?.find(bt => bt.active);
        if (settings?.useThemeForLandlord && theme) {
          const activeTheme = {
            logo: settings.logo,
            favicon: settings.favicon,
            theme: {
              ...theme
            }
          };
          this.themeService.createTheme(activeTheme);
          this.themeService.setTheme(activeTheme.theme.name);
        } else {
          this.themeService.setTheme(
            (environment as EnvironmentConfig).app_design
          );
        }
      });
  }

  ngOnInit(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(
          filter(
            (evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'
          )
        )
        .subscribe(() =>
          this.modalService
            .open(NewUpdateBannerComponent, {
              backdrop: 'static'
            })
            .onClose()
            .subscribe(() => this.store.dispatch(new UpdateApplication()))
        );
    }

    this.store
      .select(fromAccountState.getUserData)
      .pipe(
        filter(userData => !!userData?.id),
        take(1)
      )
      .subscribe(() => {
        // If the user is logged in, show the adblock hint
        this.adBlockDetectorService.checkForAdsJs(this.viewContainerRef);
      });
    this.store.dispatch(new InitCookiesPreference());
  }
}
